import React, {PropsWithChildren, useContext} from "react";

import {ApolloClient, ApolloLink, ApolloProvider} from "@apollo/client";
import {WebSocketLink} from "@apollo/client/link/ws";
import {InMemoryCache} from "@apollo/client/cache";
import {getAccessToken, GlobalContext} from "../../state/GlobalProvider";
import {onError} from "@apollo/client/link/error";
import {SubscriptionClient} from "subscriptions-transport-ws";
import {throttle} from "../../util/throttle";

export default function GraphQLProvider(props: PropsWithChildren<{}>) {

    const globalCtx = useContext(GlobalContext);
    const { auth: { token }, updateAuth, setWsConnected } = globalCtx;

    const errorLink = React.useMemo(() => {
        const link = onError(({graphQLErrors, networkError}) => {
            if (graphQLErrors) {
                graphQLErrors.map(({message}) => {
                    // On unauthorized response we want to attempt a refresh of the token.  This is throttled to prevent
                    // infinite loops in the case of a misconfiguration.
                    if (message === 'UNAUTHENTICATED') {
                        throttle(() => (async function () {
                                updateAuth(await getAccessToken());
                            }()),
                            "graphql-reauthorize", 5000);
                    }
                });
            }

            // @apollo/client/link/ws is currently just a thin wrapper for subscription-transport-ws which handles
            // errors through the SubscriptionClient events.
            if (networkError) {
                console.log(`[Network error]: ${networkError}`);
            }
        });
        return link;
    }, [ updateAuth ]);

    const graphLink = React.useMemo(() => {
        const client = new SubscriptionClient(`wss://` + window.location.hostname + `/api/v1/graphql`,
        {
            reconnect: true,
            connectionParams: {
                authorization: token,
            },
            lazy: true,
        });

        client.onConnected(() => setWsConnected(true));
        client.onDisconnected(() => setWsConnected(false));
        client.onReconnected(() => setWsConnected(true));

        return new WebSocketLink(client);
    }, [token, setWsConnected]);

    const client = React.useMemo( () => new ApolloClient({
        link: ApolloLink.from([errorLink, graphLink]),
        cache: new InMemoryCache({}),
        defaultOptions: {
            watchQuery: {
                fetchPolicy: "no-cache"
            }
        }

    }), [ errorLink, graphLink ]);

    return <ApolloProvider client={client}>
        {props.children}
    </ApolloProvider>
}